import React, { createContext, useContext, useEffect, useRef, useState } from "react";

import { IconButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Layouts } from "react-grid-layout";
import { useTranslation } from "react-i18next";
import { CgEye } from "react-icons/cg";
import { useHistory } from "react-router-dom";

import {
  PRIVATE_ROUTES,
  PURCHASE_ORDERS_STATUS_COLORS,
  SHIPMENT_TRACKING_STATUS_COLORS,
} from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { useQueryURL } from "../../../hooks/useQueryURL";
import useTimezoneDate from "../../../hooks/useTimezoneDate";
import {
  IDashboardGraph,
  IDashboardQueryParams,
  IPeriod,
  ISupplyChainDashboard,
} from "../../../models/overview/dashboard";
import {
  ContainersConfigure,
  ESupplyChainOverviewTabs,
  OrdersConfigure,
  ProductsConfigure,
  ShipmentsConfigure,
  SupplyChainOverviewConfigure,
} from "../../../models/supply.chain.overview.configure";
import ShipmentSideView, {
  ShipmentViewHandles,
} from "../../../pages/private/SupplyChainPlus/Shipments/View";
import DashboardService, { ContainerByArrivalDate } from "../../../services/dashboard.service";
import { DashboardPeriodOptions, OrderStatus } from "../../../util/enums";
import { formatDate, formatDateWithoutTimeZone } from "../../../util/Format";
import { DashboardKeys } from "../../../util/queries";
import {
  defaultConfig,
  getLocalStorageConfig,
  getLocalStorageLastCustomDate,
  getLocalStorageLastCustomPeriod,
  getLocalStorageLastPeriod,
  getLocalStorageLastTab,
  setLocalStorageConfig,
} from "./LocalStorageConfig";
import { ContainerNumberLabel } from "./styles";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour
const STALE_TIME = 2 * 60 * 1000; // 2 minutes

interface SupplyChainOverviewContextProps {
  config: SupplyChainOverviewConfigure;
  customDate: Date | undefined;
  customPeriod: IPeriod | undefined;
  dashboard: ISupplyChainDashboard;
  getConfigByPanel<
    T extends ContainersConfigure | OrdersConfigure | ProductsConfigure | ShipmentsConfigure
  >(
    panel: keyof SupplyChainOverviewConfigure
  ): T;
  handleLayoutChange: (panel: keyof SupplyChainOverviewConfigure, all: Layouts) => void;
  handleWidgetConfigChange: (
    panel: keyof SupplyChainOverviewConfigure,
    widget: string,
    configValue: boolean
  ) => void;
  layouts: Layouts;
  resetConfigToDefaut: () => void;
  selectedPanel: ESupplyChainOverviewTabs;
  selectedPeriod: DashboardPeriodOptions;
  setConfig: (value: SupplyChainOverviewConfigure) => void;
  setCustomDate: (value: Date) => void;
  setCustomPeriod: (value: IPeriod) => void;
  setDashboard: (value: ISupplyChainDashboard) => void;
  setSelectedPanel: (value: ESupplyChainOverviewTabs) => void;
  setSelectedPeriod: (value: DashboardPeriodOptions) => void;
  widgetLayoutBackup: (dashboard: keyof SupplyChainOverviewConfigure, i: string) => void;
  widgetLayoutRestore: (dashboard: keyof SupplyChainOverviewConfigure, i: string) => void;
}

const SupplyChainOverviewContext = createContext<SupplyChainOverviewContextProps>(
  {} as SupplyChainOverviewContextProps
);

interface SupplyChainOverviewProviderProps {
  children: React.ReactChild;
}

interface SearchParams {
  period?: DashboardPeriodOptions;
  tab?: ESupplyChainOverviewTabs;
}

const SupplyChainOverviewProvider = ({ children }: SupplyChainOverviewProviderProps) => {
  const { currentUser } = useAppState();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const { getDateValueUtcTimezone } = useTimezoneDate();

  const shipmentSideViewRef = useRef<ShipmentViewHandles>(null);

  const queries = useQueryURL<SearchParams>();
  const { period } = queries("period");
  const { tab } = queries("tab");

  const renderBtnViewLastReceivedOrder = (id: number | string) => {
    const viewOrder = (id: number | string) => {
      history.push({
        pathname: `${PRIVATE_ROUTES.ORDERS_MNT}/${id}`,
        state: { viewOnly: true },
      });
    };

    return (
      <IconButton onClick={() => viewOrder(id)} size="small">
        <CgEye />
      </IconButton>
    );
  };

  const renderBtnViewProduct = (id: number | string) => {
    const viewProduct = (id: number | string) => {
      history.push({
        pathname: `${PRIVATE_ROUTES.PRODUCTS_MNT}/${id}`,
        state: { productId: id, viewOnly: true },
      });
    };

    return (
      <IconButton onClick={() => viewProduct(id)} size="small">
        <CgEye />
      </IconButton>
    );
  };

  const renderViewShipment = (containerNumber: string, shipmentId: number) => {
    const viewShipment = (shipmentId: number) => {
      shipmentSideViewRef.current?.open(Number(shipmentId));
    };

    return (
      <>
        <ContainerNumberLabel onClick={() => viewShipment(shipmentId)}>
          {containerNumber}
        </ContainerNumberLabel>

        <ShipmentSideView ref={shipmentSideViewRef} />
      </>
    );
  };

  const [config, setConfig] = useState<SupplyChainOverviewConfigure>(getLocalStorageConfig());

  const lastCustomDate = getLocalStorageLastCustomDate();
  const lastCustomPeriod = getLocalStorageLastCustomPeriod();
  const lastPeriod = getLocalStorageLastPeriod();
  const lastTab = getLocalStorageLastTab();

  const formatQueryDate = (date?: Date): string | undefined => {
    return date ? format(date, "yyyy-MM-dd") : undefined;
  };

  const getQueryEnabled = (
    selectedPeriod: DashboardPeriodOptions,
    customDate?: Date,
    customPeriod?: IPeriod
  ): boolean => {
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_DATE && !customDate) return false;
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_PERIOD && !customPeriod) return false;

    return !!currentUser?.companyId;
  };

  const getContaintersGraphInbound = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getShipmentsContainers({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const { inBounds, total } = response.data;

        return { datasets: [{ data: [inBounds] }], labels: ["inBounds"], total } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getContaintersGraphInbound(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getContaintersGraphAtPort = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getShipmentsContainers({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const { atPort, total } = response.data;

        return { datasets: [{ data: [atPort] }], labels: ["atPort"], total } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getContaintersGraphAtPort(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getContaintersGraphAtDC = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getShipmentsContainers({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const { atDc, total } = response.data;

        return { datasets: [{ data: [atDc] }], labels: ["atDc"], total } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getContaintersGraphAtDC(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getContaintersGraphDistributionByDestination = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getContainersCountGroupedByDestination({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const datasets = [{ data: new Array<number>() }];
        const labels: string[] = [];

        response.data.forEach((d: any) => {
          datasets[0].data.push(d.count);
          labels.push(d.dc_destination.toUpperCase());
        });

        return { datasets, labels } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getContaintersGraphDistributionByDestination(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getContaintersGraphStatuses = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getContainersCountGroupedByStatus({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const { data } = response;
        const colors: string[] = [];
        const datasets = [{ data: new Array<number>() }];
        const labels: string[] = [];

        data.forEach((d: any) => {
          colors.push(SHIPMENT_TRACKING_STATUS_COLORS[d.status_shipment.toLowerCase()]?.text);
          datasets[0].data.push(d.count);
          labels.push(d.status_shipment.toUpperCase());
        });

        return { colors, datasets, labels } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getContaintersGraphStatuses(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getContaintersGraphByArrivalDate = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getContainersByArrivalDate({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        return {
          datasets: response.data.map((dataset: ContainerByArrivalDate) => ({
            ...dataset,
            arrivalDate: formatDate(String(dataset.arrivalDate)),
          })),
          labels: [
            {
              name: "containerNumber",
              title: translate("containerNumber"),
            },
            {
              formatComponent: (value: string, row: ContainerByArrivalDate) =>
                renderViewShipment(value, row?.shipmentId),
              name: "shipmentNumber",
              title: translate("shipmentNumber"),
            },
            { name: "arrivalDate", title: translate("arrivalDate") },
          ],
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getContaintersGraphByArrivalDate(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getOrdersGraphPurchaseOrders = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getPurchaseOrdersCount({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
          status: [OrderStatus.RECEIVED],
          withTotal: true,
        });

        if (!response.data) return;

        return {
          datasets: [{ data: response.data.count || 0 }],
          labels: ["pendingPurchaseOrders"],
          total: response.data.total || 0,
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getOrdersGraphPurchaseOrders(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getOrdersGraphShipByDate = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getPurchaseOrdersCount({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
          pastShipByDate: true,
          status: [OrderStatus.ASSIGNED, OrderStatus.RECEIVED],
          withTotal: true,
        });

        if (!response.data) return;

        return {
          datasets: [{ data: response.data.count || 0 }],
          labels: ["poPastShipByDate"],
          total: response.data.total || 0,
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getOrdersGraphShipByDate(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getOrdersGraphPoStatuses = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getPurchaseOrdersCountGroupedByStatus({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const { data } = response;
        const colors: string[] = [];
        const datasets = [{ data: new Array<number>() }];
        const labels: string[] = [];

        data.forEach((d: any) => {
          colors.push(PURCHASE_ORDERS_STATUS_COLORS[d.status]?.text);
          datasets[0].data.push(d.count);
          labels.push(d.status.toUpperCase());
        });

        return { colors, datasets, labels } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getOrdersGraphPoStatuses(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getOrdersGraphLastReceivedOrders = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getLastReceivedPurchaseOrders({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        return {
          datasets: response.data.rows,
          labels: [
            {
              name: "reference",
              title: `${translate("order")} #`,
            },
            {
              name: "eta",
              title: translate("shipByDate"),
              formatText: (value?: string) => formatDateWithoutTimeZone(value),
            },
            {
              name: "supplierName",
              title: translate("supplier"),
            },
            {
              name: "unitQuantity",
              title: translate("unitQty"),
            },
          ],
          rowActions: [
            {
              title: translate("view"),
              buttonAndAction: renderBtnViewLastReceivedOrder,
            },
          ],
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getLastReceivedPurchaseOrders(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getOrdersGraphPoConfirmedVSPoReceived = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getPurchaseOrdersCountGroupedBy({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const { data } = response;
        const colors = [
          PURCHASE_ORDERS_STATUS_COLORS[OrderStatus.CONFIRMED].text,
          PURCHASE_ORDERS_STATUS_COLORS[OrderStatus.RECEIVED].text,
        ];
        const dataLabels = [OrderStatus.CONFIRMED, OrderStatus.RECEIVED];
        const labelsSet: Set<string> = new Set(data.map(({ label }) => label || ""));
        const dataMap: Map<string, number[]> = new Map(dataLabels.map((label) => [label, []]));

        labelsSet.forEach((label: string) => {
          dataLabels.forEach((dataLabel) => {
            const values = dataMap.get(dataLabel) || [];
            const count = data.find((d) => d.status === dataLabel && d.label === label)?.count;

            dataMap.set(dataLabel, [...values, count || 0]);
          });
        });

        return {
          colors,
          datasets: dataLabels.map((l) => ({ label: l.toUpperCase(), data: dataMap.get(l) || [] })),
          labels: Array.from(labelsSet).map((l: string) => l.toUpperCase()),
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getOrdersGraphPoConfirmedVSPoReceived(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getProductsGraphOrigin = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getProductsCountGroupedByCountry({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const data = response.data.map((d: any) => ({ code: d.country_code, value: d.count }));

        return { datasets: [{ data }] } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getProductsGraphOrigin(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getProductsGraphMostRequested = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getProductsMostRequested({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        return {
          datasets: response.data,
          labels: [
            { name: "sku", title: `${translate("sku")} #` },
            { name: "currentStock", title: translate("currentStock") },
            { name: "cartonsToDespatch", title: translate("cartonsToDespatch") },
          ],
          rowActions: [{ title: translate("view"), buttonAndAction: renderBtnViewProduct }],
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getProductsGraphMostRequested(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getProductsGraphRunningOutOfStock = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getProductsRunningOutOfStock({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        return {
          datasets: response.data.map((d: any) => ({ ...d, currentStock: d.stock || 0 })),
          labels: [
            { name: "sku", title: `${translate("sku")} #` },
            { name: "currentStock", title: translate("currentStock") },
          ],
          rowActions: [{ title: translate("view"), buttonAndAction: renderBtnViewProduct }],
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getProductsGraphRunningOutOfStock(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getShipmentsTrackeds = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getShipmentsTrackeds({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response?.data) return;

        return {
          datasets: [{ data: response.data }],
          labels: response.data.map(
            (d: any) => `${translate("amountOfSSCC")} (VesselID = ${d.label})`
          ),
        } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getShipmentsTrackeds(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const getShipmentGraphCountPerTime = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getShipmentsCountGroupedByTime({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response?.data) return;

        const datasets = [{ data: new Array<number>() }];
        const labels: string[] = [];

        response.data.forEach((d: any) => {
          datasets[0].data.push(d.count);
          labels.push(d.label.toUpperCase());
        });

        return { datasets, labels } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getShipmentGraphCountPerTime(
        customDate,
        customPeriod,
        selectedPeriod
      ),
      staleTime: STALE_TIME,
    });
  };

  const getShipmentsGraphStatuses = ({
    customDate,
    customPeriod,
    selectedPeriod,
  }: IDashboardQueryParams) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: getQueryEnabled(selectedPeriod, customDate, customPeriod),
      queryFn: async () => {
        const response = await DashboardService.getShipmentsCountGroupedByStatus({
          companyId: currentUser?.companyId,
          dateFilterType: selectedPeriod,
          dateFilter: getDateValueUtcTimezone(customDate?.toISOString(), "yyyy-MM-dd"),
          dateFrom: formatQueryDate(customPeriod?.from),
          dateTo: formatQueryDate(customPeriod?.to),
        });

        if (!response.data) return;

        const colors: string[] = [];
        const datasets = [{ data: new Array<number>() }];
        const labels: string[] = [];

        response.data.forEach((d: any) => {
          colors.push(SHIPMENT_TRACKING_STATUS_COLORS[d.statusShipment.toLowerCase()]?.text);
          datasets[0].data.push(d.count);
          labels.push(d.statusShipment.toUpperCase());
        });

        return { colors, datasets, labels } as IDashboardGraph;
      },
      queryKey: DashboardKeys.getShipmentsGraphStatuses(customDate, customPeriod, selectedPeriod),
      staleTime: STALE_TIME,
    });
  };

  const [dashboard, setDashboard] = useState<ISupplyChainDashboard>({
    containers: {
      graphs: {
        inbound: {
          datasets: [],
          title: "inbound",
          type: "ratio",
          useQuery: getContaintersGraphInbound,
        },
        atPort: {
          datasets: [],
          title: "atPort",
          type: "ratio",
          useQuery: getContaintersGraphAtPort,
        },
        atDC: {
          datasets: [],
          title: "atDC",
          type: "ratio",
          useQuery: getContaintersGraphAtDC,
        },
        containerDistributionByDestination: {
          datasets: [],
          title: "containerDistributionByDestination",
          type: "bar",
          useQuery: getContaintersGraphDistributionByDestination,
        },
        containersStatuses: {
          datasets: [],
          title: "containersStatuses",
          type: "doughnut",
          useQuery: getContaintersGraphStatuses,
        },
        containersByArrivalDate: {
          datasets: [],
          title: "containersByArrivalDate",
          type: "table",
          useQuery: getContaintersGraphByArrivalDate,
        },
      },
    },
    orders: {
      graphs: {
        pendingPurchaseOrders: {
          datasets: [],
          title: "pendingPurchaseOrders",
          type: "ratio",
          useQuery: getOrdersGraphPurchaseOrders,
        },
        shipByDate: {
          datasets: [],
          title: "poPastShipByDate",
          type: "ratio",
          useQuery: getOrdersGraphShipByDate,
        },
        poStatuses: {
          datasets: [],
          title: "poStatuses",
          type: "doughnut",
          useQuery: getOrdersGraphPoStatuses,
        },
        lastReceivedOrders: {
          datasets: [],
          title: "lastReceivedOrders",
          type: "table",
          useQuery: getOrdersGraphLastReceivedOrders,
        },
        poConfirmedVSPoReceived: {
          datasets: [],
          title: "poConfirmedVSPoReceived",
          type: "bar",
          useQuery: getOrdersGraphPoConfirmedVSPoReceived,
        },
      },
    },
    products: {
      graphs: {
        productsOrigin: {
          datasets: [],
          title: "productsOrigin",
          type: "map",
          useQuery: getProductsGraphOrigin,
        },
        mostRequestedProducts: {
          datasets: [],
          title: "mostRequestedProducts",
          type: "table",
          useQuery: getProductsGraphMostRequested,
        },
        productsRunningOutOfStock: {
          datasets: [],
          title: "productsRunningOutOfStock",
          type: "table",
          useQuery: getProductsGraphRunningOutOfStock,
        },
      },
    },
    shipments: {
      graphs: {
        shipmentsTracked: {
          datasets: [],
          title: "shipmentsTracked",
          type: "bubblemap",
          useQuery: getShipmentsTrackeds,
        },
        shipmentsPerDate: {
          datasets: [],
          title: "shipmentsPerDate",
          type: "line",
          useQuery: getShipmentGraphCountPerTime,
        },
        shipmentStatuses: {
          datasets: [],
          title: "shipmentStatuses",
          type: "doughnut",
          useQuery: getShipmentsGraphStatuses,
        },
      },
    },
  });

  const [selectedPanel, setSelectedPanel] = useState<ESupplyChainOverviewTabs>(tab || lastTab);

  const [selectedPeriod, setSelectedPeriod] = useState<DashboardPeriodOptions>(
    period || lastPeriod
  );

  const [customDate, setCustomDate] = useState<Date | undefined>(lastCustomDate);

  const [customPeriod, setCustomPeriod] = useState<IPeriod | undefined>(lastCustomPeriod);

  const { layouts } = config[selectedPanel];

  const handleLayoutChange = (panel: keyof SupplyChainOverviewConfigure, all: Layouts) => {
    setConfig((state) => ({ ...state, [panel]: { ...state[panel], layouts: { ...all } } }));
  };

  const resetConfigToDefaut = () => {
    setConfig(defaultConfig);
  };

  const widgetLayoutBackup = (dashboard: keyof SupplyChainOverviewConfigure, i: string) => {
    const { layouts, layoutsDisabled } = config[dashboard];

    Object.keys(layouts).forEach((layoutKey) => {
      const layout = layouts[layoutKey];
      const layoutProps = layout.find((l) => l.i === i);

      if (layoutProps) {
        layoutsDisabled[layoutKey] = [...(layoutsDisabled[layoutKey] || []), layoutProps];
      }
    });
  };

  const widgetLayoutRestore = (dashboard: keyof SupplyChainOverviewConfigure, i: string) => {
    const { layouts, layoutsDisabled } = config[dashboard];

    Object.keys(layoutsDisabled).forEach((layoutKey) => {
      const layout = layoutsDisabled[layoutKey];
      const propsIdx = layout.findIndex((l) => l.i === i);

      if (propsIdx >= 0) {
        layouts[layoutKey] = [...(layouts[layoutKey] || []), layout[propsIdx]];
        layout.splice(propsIdx, 1);
      }
    });
  };

  const handleWidgetConfigChange = (
    panel: keyof SupplyChainOverviewConfigure,
    widget: string,
    configValue: boolean
  ) => {
    const panelConfig = { ...config[panel], [widget]: configValue };

    const { layouts, layoutsDisabled } = { ...panelConfig };

    if (configValue) {
      Object.keys(layoutsDisabled).forEach((layoutKey) => {
        const layout = layoutsDisabled[layoutKey];
        const propsIdx = layout.findIndex((l) => l.i === widget);

        if (propsIdx >= 0) {
          layouts[layoutKey] = [...(layouts[layoutKey] || []), layout[propsIdx]];
          layout.splice(propsIdx, 1);
        }
      });
      setConfig({ ...config, [panel]: panelConfig });
      setTimeout(() => handleLayoutChange(panel, { ...layouts }), 200); // Wainting save configValue and then handling layout change.

      return;
    }

    Object.keys(layouts).forEach((layoutKey) => {
      const layout = layouts[layoutKey];
      const layoutProps = layout.find((l) => l.i === widget);

      if (layoutProps) {
        layoutsDisabled[layoutKey] = [...(layoutsDisabled[layoutKey] || []), layoutProps];
      }
    });
    setConfig({ ...config, [panel]: panelConfig });
  };

  const getConfigByPanel = <
    T extends ContainersConfigure | OrdersConfigure | ProductsConfigure | ShipmentsConfigure
  >(
    panel: keyof SupplyChainOverviewConfigure
  ): T => {
    return config[panel] as T;
  };

  useEffect(() => {
    if (selectedPeriod !== DashboardPeriodOptions.CUSTOM_DATE) setCustomDate(undefined);
    if (selectedPeriod !== DashboardPeriodOptions.CUSTOM_PERIOD) setCustomPeriod(undefined);
  }, [selectedPeriod]);

  useEffect(() => {
    setLocalStorageConfig(config);
  }, [config]);

  return (
    <SupplyChainOverviewContext.Provider
      value={{
        config,
        customDate,
        customPeriod,
        dashboard,
        getConfigByPanel,
        handleLayoutChange,
        handleWidgetConfigChange,
        layouts,
        resetConfigToDefaut,
        selectedPanel,
        selectedPeriod,
        setConfig,
        setCustomDate,
        setCustomPeriod,
        setDashboard,
        setSelectedPanel,
        setSelectedPeriod,
        widgetLayoutBackup,
        widgetLayoutRestore,
      }}
    >
      {children}
    </SupplyChainOverviewContext.Provider>
  );
};

export { SupplyChainOverviewContext, SupplyChainOverviewProvider };

export const useSupplyChainOverviewContext = () => {
  return useContext(SupplyChainOverviewContext);
};
